import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Globe, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Switch } from '@/components/settings/settings-controls';
import { listTimeZones } from '@/lib/timezone';
import { cloneWeekly, emptyWeekly, weeklySummary } from '@/lib/bot-service-hours';
import { WeeklyHoursGrid } from './weekly-hours-grid';
import { OutOfHoursMessageEditor } from './out-of-hours-message-editor';

// Editor for one nullable override scope (the in-app chat channel, or a WhatsApp-number
// row): every
// field has an "Override" switch — off = null = inherit, shown as a muted "Inherits default:
// …" summary; on reveals the control seeded from the inherited value. Mode + message travel
// as one row because they are one decision (what happens out of hours). Controlled: onChange
// receives a partial patch of the override object (null = back to inherit).

const pretty = (zone) => String(zone || '').replace(/_/g, ' ');

/** Searchable IANA-zone picker (the timezone-card combobox, minus the system-default row). */
export function TimezoneCombobox({ value, disabled = false, onChange, ariaLabel = 'Timezone' }) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => listTimeZones(), []);
  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          className="h-8 w-64 justify-between text-left text-sm font-normal"
        >
          <span className="flex min-w-0 items-center gap-1.5">
            <Globe className="h-3.5 w-3.5 shrink-0 opacity-60" />
            <span className="truncate">{value ? pretty(value) : 'Select a timezone'}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Search timezones…" />
          <CommandList>
            <CommandEmpty>No matching timezone.</CommandEmpty>
            <CommandGroup>
              {zones.map((zone) => (
                <CommandItem
                  key={zone}
                  value={`${zone} ${pretty(zone)}`}
                  onSelect={() => {
                    onChange(zone);
                    setOpen(false);
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === zone ? 'opacity-100' : 'opacity-0')} />
                  <span className="truncate">{pretty(zone)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function FieldRow({ label, description, overridden, disabled, inheritSummary, onToggle, children }) {
  return (
    <div className="border-b border-border px-3 py-2.5 last:border-b-0">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-foreground">{label}</p>
          {overridden ? (
            description ? (
              <p className="text-[11px] text-muted-foreground">{description}</p>
            ) : null
          ) : (
            <p className="truncate text-[11px] text-muted-foreground">
              Inherits default: {inheritSummary}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Override
          </span>
          <Switch
            checked={overridden}
            disabled={disabled}
            label={`Override ${label}`}
            onChange={onToggle}
          />
        </div>
      </div>
      {overridden ? <div className="mt-2.5">{children}</div> : null}
    </div>
  );
}

export function OverrideEditor({
  value,
  inherited,
  placeholders = [],
  errors = {},
  errorPrefix,
  disabled = false,
  onChange,
}) {
  const modeOverridden = value.mode !== null || value.message !== null;
  const anyOverridden =
    value.enabled !== null ||
    value.timezone !== null ||
    value.weekly !== null ||
    value.closeOnPublicHolidays !== null ||
    modeOverridden;

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-lg border border-border bg-card">
        <FieldRow
          label="Working-hours gate"
          description="Whether closed-hours handling applies on this channel at all."
          overridden={value.enabled !== null}
          disabled={disabled}
          inheritSummary={inherited.enabled ? 'On' : 'Off'}
          onToggle={(next) => onChange({ enabled: next ? inherited.enabled : null })}
        >
          <div className="flex items-center gap-2.5">
            <Switch
              checked={Boolean(value.enabled)}
              disabled={disabled}
              label="Working-hours gate on this channel"
              onChange={(next) => onChange({ enabled: next })}
            />
            <span className="text-xs text-muted-foreground">
              {value.enabled
                ? 'Closed-hours messages are held here.'
                : 'Off — this channel answers around the clock.'}
            </span>
          </div>
        </FieldRow>

        <FieldRow
          label="Timezone"
          overridden={value.timezone !== null}
          disabled={disabled}
          inheritSummary={pretty(inherited.timezone) || '—'}
          onToggle={(next) => onChange({ timezone: next ? inherited.timezone : null })}
        >
          <TimezoneCombobox
            value={value.timezone || ''}
            disabled={disabled}
            onChange={(zone) => onChange({ timezone: zone })}
          />
          {errors[`${errorPrefix}.timezone`] ? (
            <p className="mt-1 text-[11px] text-destructive">{errors[`${errorPrefix}.timezone`]}</p>
          ) : null}
        </FieldRow>

        <FieldRow
          label="Weekly hours"
          overridden={value.weekly !== null}
          disabled={disabled}
          inheritSummary={weeklySummary(inherited.weekly)}
          onToggle={(next) =>
            onChange({ weekly: next ? cloneWeekly(inherited.weekly) : null })
          }
        >
          <WeeklyHoursGrid
            weekly={value.weekly || emptyWeekly()}
            errors={errors}
            errorPrefix={`${errorPrefix}.weekly`}
            disabled={disabled}
            onChange={(weekly) => onChange({ weekly })}
          />
        </FieldRow>

        <FieldRow
          label="Public holidays"
          overridden={value.closeOnPublicHolidays !== null}
          disabled={disabled}
          inheritSummary={
            inherited.closeOnPublicHolidays ? 'Closed on public holidays' : 'Open on public holidays'
          }
          onToggle={(next) =>
            onChange({ closeOnPublicHolidays: next ? inherited.closeOnPublicHolidays : null })
          }
        >
          <div className="flex items-center gap-2.5">
            <Switch
              checked={Boolean(value.closeOnPublicHolidays)}
              disabled={disabled}
              label="Closed on public holidays"
              onChange={(next) => onChange({ closeOnPublicHolidays: next })}
            />
            <span className="text-xs text-muted-foreground">
              {value.closeOnPublicHolidays ? 'Closed on public holidays.' : 'Open on public holidays.'}
            </span>
          </div>
        </FieldRow>

        <FieldRow
          label="Out-of-hours reply"
          description="Leave the message empty to reuse the default message."
          overridden={modeOverridden}
          disabled={disabled}
          inheritSummary={
            inherited.mode === 'silent' ? 'Silent — no reply' : 'Reply with the default message'
          }
          onToggle={(next) =>
            onChange(next ? { mode: inherited.mode } : { mode: null, message: null })
          }
        >
          <OutOfHoursMessageEditor
            mode={value.mode ?? inherited.mode}
            message={value.message ?? ''}
            placeholders={placeholders}
            disabled={disabled}
            errors={{
              mode: errors[`${errorPrefix}.mode`],
              message: errors[`${errorPrefix}.message`],
            }}
            messagePlaceholder="Leave empty to use the default message."
            onChange={(patch) => {
              // A blank override message means "inherit", so it is stored as null — exactly
              // the wire semantics, and it keeps the dirty check honest.
              if ('message' in patch) onChange({ message: patch.message === '' ? null : patch.message });
              else onChange(patch);
            }}
          />
        </FieldRow>
      </div>
      {anyOverridden && !disabled ? (
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="[&_svg]:size-3.5"
            onClick={() =>
              onChange({
                enabled: null,
                timezone: null,
                weekly: null,
                closeOnPublicHolidays: null,
                mode: null,
                message: null,
              })
            }
          >
            <RotateCcw />
            Reset to defaults
          </Button>
        </div>
      ) : null}
    </div>
  );
}
